import { TextField, Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const PriceInputCell = ({
                                   value,
                                   onChange,
                                   inputId,
                                   headCells,
                                   min = 0,
                                   required = false,
                                   debounceMs = 500,
                               }) => {
    const inputRef = useRef();
    const [isFocused, setIsFocused] = useState(false);
    const [localValue, setLocalValue] = useState('');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    const debounceTimer = useRef(null);
    const feedbackTimer = useRef(null);

    useEffect(() => {
        if (!isFocused) {
            setLocalValue(value ?? '');
        }
    }, [value, isFocused]);

    const validate = (val) => {
        const num = Number(val);
        if (required && !val) return 'Pflichtfeld';
        if (val && !/^\d+$/.test(val)) return 'Nur Zahlen erlaubt';
        if (val && num < min) return `Mindestens $${min}`;
        return '';
    };

    const saveValue = (val) => {
        const validation = validate(val);
        setError(validation);

        if (val === String(value)) return;

        if (!validation) {
            onChange(val);
            setSaved(true);
            clearTimeout(feedbackTimer.current);
            feedbackTimer.current = setTimeout(() => setSaved(false), 1000);
        }
    };

    const handleFocus = (e) => {
        e.target.select();
        setIsFocused(true);
        setLocalValue(value ?? '');
        setError('');
    };

    const handleBlur = () => {
        setIsFocused(false);
        saveValue(localValue);
    };

    const handleChange = (e) => {
        const raw = e.target.value.replace(/[^\d]/g, '');
        setLocalValue(raw);
        setError('');

        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => saveValue(raw), debounceMs);
    };

    const handleKeyDown = (e) => {
        if (!inputId || !headCells) return;

        const idMatch = inputId.match(/^input-(\d+)-(.+)$/);
        if (!idMatch) return;

        const [_, rowIndex, columnKey] = idMatch;
        const row = parseInt(rowIndex, 10);
        const colIndex = headCells.findIndex((h) => h.id === columnKey);

        const getTargetId = (r, c) =>
            headCells[c] ? `input-${r}-${headCells[c].id}` : null;

        switch (e.key) {
            case 'ArrowRight': {
                const nextId = getTargetId(row, colIndex + 1);
                if (nextId) {
                    document.getElementById(nextId)?.focus();
                    e.preventDefault();
                }
                break;
            }
            case 'ArrowLeft': {
                const prevId = getTargetId(row, colIndex - 1);
                if (prevId) {
                    document.getElementById(prevId)?.focus();
                    e.preventDefault();
                }
                break;
            }
            case 'ArrowDown': {
                const downId = getTargetId(row + 1, colIndex);
                if (downId) {
                    document.getElementById(downId)?.focus();
                    e.preventDefault();
                }
                break;
            }
            case 'ArrowUp': {
                const upId = getTargetId(row - 1, colIndex);
                if (upId) {
                    document.getElementById(upId)?.focus();
                    e.preventDefault();
                }
                break;
            }
        }
    };

    const displayValue = !isFocused
        ? (value && Number(value) !== 0 && !isNaN(value)
            ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
            }).format(Number(value))
            : '')
        : localValue;

    return (
        <Box display="flex" flexDirection="column" alignItems="start">
            <TextField
                id={inputId}
                variant="standard"
                value={displayValue}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                inputRef={inputRef}
                error={!!error}
                helperText={error}
                slotProps={{
                    input: {
                        disableUnderline: true,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        tabIndex: 0,
                        sx: {
                            fontWeight: 'bold',
                            fontSize: '0.95rem',
                            width: '60px',
                            cursor: 'text',
                            transition: 'background-color 0.3s, border 0.3s',
                            border: saved ? '1px solid #4caf50' : '1px solid transparent',
                            borderRadius: '4px',
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'transparent'
                                    : saved
                                        ? '#e8f5e9'
                                        : 'transparent',
                            '&:hover': {
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.06)'
                                        : 'rgba(0,0,0,0.04)',
                            },
                            '& input:focus': {
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.08)'
                                        : 'rgba(0,0,0,0.04)',
                            },
                        },
                    },
                }}
            />
        </Box>
    );
};
